import React, { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useAccess } from '@/lib/access';
import { useCycle } from '@/lib/cycle';
import { PageHeader, Card, SectionBadge, Loading, Empty } from '@/components/UI';
import { formatDateTime } from '@/lib/format';
import { Upload as UploadIcon, CheckCircle2, AlertCircle, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { API_BASE } from '@/lib/api';
import { toast } from 'sonner';

const SOURCES = [
  { key: 'production', label: 'Production', help: 'Upload the Production app JSON backup (.json or .json.gz). Data is split per AAMPL unit automatically.', dept: 'production', sections: ['production'] },
  { key: 'maintenance', label: 'Maintenance', help: 'Upload the Maintenance app JSON backup. PM compliance, breakdowns and spare alerts are split per unit.', dept: 'maintenance', sections: ['maintenance'] },
  { key: 'npd', label: 'NPD / RFQ', help: 'Upload the NPD app JSON backup. RFQ funnel and costings are split by unit.', dept: 'npd', sections: ['npd'] },
  { key: 'hr', label: 'HR', help: 'Upload the HR app JSON backup. Headcount, joiners, exits, suggestions, kaizens, appraisals and salary are mapped automatically per unit.', dept: 'hr_safety', sections: ['hr'] },
  { key: 'hr_monthly', label: 'HR Monthly Sheet', help: 'Attendance, training and safety are not in the HR software. Download the template, fill one row per unit once a month, and upload it here (.xlsx or .csv). Blank = not known, 0 = zero.', dept: 'hr_safety', sheet: true, sections: ['hr'] },
];

const humanize = (k) => String(k).replace(/_pct$/, ' %').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).replace('Lti', 'LTI').replace('Ot ', 'OT ').replace('Pf ', 'PF ').replace('Esi ', 'ESI ');

export default function UploadCenter() {
  const { user } = useAuth();
  const { can, canEdit } = useAccess();
  const { currentCycle, currentUnit } = useCycle();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);

  const year = currentCycle?.year;
  const month = currentCycle?.month;

  const refresh = async () => {
    if (!year || !month) return;
    setLoading(true);
    try { const r = await api.get(`/upload/${year}/${month}`); setUploads(r.data || []); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [year, month]);

  if (!currentCycle) return <Empty title="No active cycle" hint="Create a cycle first." />;

  // One rule for every source card: the section that owns the source (section master) decides.
  const visibleSources = SOURCES.filter((s) => can(...s.sections));
  const canUpload = (srcKey) => { const s = SOURCES.find((x) => x.key === srcKey); return s ? canEdit(...s.sections) : false; };
  const latestFor = (src) => uploads.find((u) => u.source_app === src && u.upload_status === 'success');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Upload Center"
        subtitle={`Uploads are shared across all unit cycles for ${['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month]} ${year}. Parsers automatically split data per AAMPL unit (U1, U3, SL).`}
        sourceBadge={currentUnit ? `Currently viewing: ${currentUnit.name} (${currentUnit.code})` : null}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
        {visibleSources.map((s) => (
          <SourceCard key={s.key} source={s} year={year} month={month} latest={latestFor(s.key)} canUpload={canUpload(s.key)} onDone={refresh} />
        ))}
        {visibleSources.length === 0 && <div className="text-sm text-slate-500 col-span-full py-6 text-center">No upload sources are part of your assigned sections.</div>}
      </div>
      <Card title="Upload History">
        {loading ? <Loading /> : uploads.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No uploads yet for this month.</div> : (
          <div className="aampl-table-wrap"><table className="aampl-table"><thead><tr><th>Date</th><th>Source</th><th>Filename</th><th>Size</th><th>Units Found</th><th>Records</th><th>Status</th><th>By</th></tr></thead><tbody>
            {uploads.map((u) => (
              <tr key={u.id}>
                <td>{formatDateTime(u.uploaded_at)}</td>
                <td className="uppercase font-semibold">{u.source_app}</td>
                <td className="max-w-xs truncate" title={u.original_filename}>{u.original_filename}</td>
                <td>{(u.file_size_bytes / 1024 / 1024).toFixed(2)} MB</td>
                <td>{(u.units_found || []).map((uf) => `${uf.code} (${uf.records})`).join(', ') || '-'}</td>
                <td>{u.total_records?.toLocaleString() || '-'}</td>
                <td><SectionBadge status={u.upload_status} label={u.upload_status} /></td>
                <td>{u.uploaded_by_name || '-'}</td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </Card>
    </div>
  );
}

function SourceCard({ source, year, month, latest, canUpload, onDone }) {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showTech, setShowTech] = useState(false);
  const fileRef = useRef(null);
  const isSheet = !!source.sheet;

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('mrm_token');
      const resp = await fetch(`${API_BASE}/upload/template/hr_monthly?year=${year}&month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('download failed');
      const blob = await resp.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `HR-Monthly-Sheet-${year}-${String(month).padStart(2, '0')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch { toast.error('Could not download the template'); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (isSheet) {
      if (!/\.(xlsx|xlsm|csv)$/i.test(file.name)) { toast.error('Upload the HR Monthly Sheet as .xlsx or .csv'); return; }
    } else if (!/\.(json|json\.gz|gz)$/i.test(file.name)) { toast.error('Upload a .json or .json.gz file'); return; }
    if (!canUpload) { toast.error('Not permitted'); return; }
    if (latest && !window.confirm(`Replace existing ${source.label} upload for ${month}/${year}?`)) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post(`/upload/${year}/${month}/${source.key}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const unitsFound = (r.data.units_found || []).map((u) => u.code).join(', ') || 'none';
      if (source.key === 'hr') {
        const kpis = r.data.hr_kpis_detected || [];
        toast.success(`HR Upload Successful — period ${r.data.period}, units: ${unitsFound}. ${kpis.length} HR KPIs auto-filled.`, { duration: 8000 });
      } else if (isSheet) {
        toast.success(`HR Monthly Sheet uploaded — units: ${unitsFound}. Attendance, training and safety figures now show on the dashboard, People & Safety page and PPT.`, { duration: 8000 });
      } else {
        toast.success(`Uploaded — ${r.data.total_records.toLocaleString()} records, units: ${unitsFound}`);
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); } finally { setBusy(false); }
  };

  return (
    <div className="aampl-card aampl-card-shadow p-5 flex flex-col" data-testid={`upload-card-${source.key}`}>
      <div className="flex items-start justify-between mb-3">
        <div><div className="text-xs uppercase tracking-wider text-slate-500">Source</div><h3 className="text-lg font-bold aampl-navy-text">{source.label}</h3></div>
        {latest ? <SectionBadge status="uploaded" label={<><CheckCircle2 size={11} className="inline" /> Uploaded</>} /> : <SectionBadge status="pending" label={<><AlertCircle size={11} className="inline" /> Pending</>} />}
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">{source.help}</p>
      {latest && (
        <div className="text-xs text-slate-600 mb-3 bg-slate-50 p-2 rounded border border-[var(--aampl-border)]">
          <div>Uploaded {formatDateTime(latest.uploaded_at)}</div>
          <div className="font-mono truncate" title={latest.original_filename}>{latest.original_filename}</div>
          <div className="mt-1">Units found: <b>{(latest.units_found || []).map((u) => `${u.code}`).join(', ') || '—'}</b></div>
          <div><b>{latest.total_records?.toLocaleString() || 0}</b> records • {(latest.file_size_bytes / 1024 / 1024).toFixed(2)} MB</div>
          {source.key === 'hr' && (latest.hr_kpis_detected || []).length > 0 && (
            <div className="mt-1.5 text-[11px]" data-testid="hr-kpis-detected">
              <span className="font-semibold text-[#15803D]"><CheckCircle2 size={11} className="inline" /> {latest.hr_kpis_detected.length} HR KPIs auto-filled</span>
              <span className="text-slate-500"> — {latest.hr_kpis_detected.slice(0, 6).map(humanize).join(', ')}{latest.hr_kpis_detected.length > 6 ? ', …' : ''}</span>
            </div>
          )}
          {source.key === 'hr' && (
            <div className="mt-1.5 text-[11px] text-[#9A3412]" data-testid="hr-monthly-hint">
              Attendance, training &amp; safety are not in the HR software → use the <b>HR Monthly Sheet</b> card. Safety counts with no incident show 0.
            </div>
          )}
          {source.key === 'hr' && (latest.hr_collections || latest.hr_unmapped || latest.hr_notes) && (
            <button type="button" className="mt-2 text-[11px] text-slate-500 inline-flex items-center gap-1" onClick={() => setShowTech((v) => !v)} data-testid="hr-tech-toggle">
              {showTech ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Technical details (file mapping)
            </button>
          )}
          {source.key === 'hr' && showTech && latest.hr_collections && Object.keys(latest.hr_collections).length > 0 && (
            <div className="mt-2 text-[11px]" data-testid="hr-collections">
              <div className="font-semibold text-slate-600 mb-0.5">Collections in file</div>
              {Object.entries(latest.hr_collections).map(([path, c]) => (
                <div key={path} className="flex items-center justify-between gap-2">
                  <span className="font-mono truncate" title={(c.sample_keys || []).join(', ')}>{path}</span>
                  <span className={`shrink-0 ${c.classified_as ? 'text-[#15803D]' : c.how === 'not recognised' ? 'text-[#DC2626] font-semibold' : 'text-slate-400'}`}>{c.classified_as ? `→ ${c.classified_as}` : c.how} · {c.rows}</span>
                </div>
              ))}
            </div>
          )}
          {source.key === 'hr' && showTech && (latest.hr_unmapped || []).length > 0 && (
            <div className="mt-2 rounded-lg border border-[#FECDD3] bg-[#FFF1F2] p-2 text-[11px] text-[#9F1239]" data-testid="hr-unmapped">
              <div className="font-semibold">Not mapped — tell us what these hold:</div>
              {latest.hr_unmapped.map((u) => <div key={u.path} className="font-mono break-all">{u.path} ({u.rows} rows): {(u.sample_keys || []).slice(0, 8).join(', ')}</div>)}
            </div>
          )}
          {source.key === 'hr' && showTech && (latest.hr_notes || []).length > 0 && (
            <ul className="mt-2 list-disc pl-4 text-[11px] text-[#C2410C] space-y-0.5" data-testid="hr-notes">
              {latest.hr_notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </div>
      )}
      {isSheet && (
        <button type="button" className="btn-secondary text-sm mb-3" onClick={downloadTemplate} data-testid="hr-monthly-template"><Download size={14} className="inline mr-1" /> Download template (.xlsx)</button>
      )}
      {canUpload ? (
        <div className={`upload-drop ${dragging ? 'dragging' : ''}`} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }} onClick={() => fileRef.current?.click()} data-testid={`upload-drop-${source.key}`}>
          <input ref={fileRef} type="file" accept={isSheet ? '.xlsx,.xlsm,.csv' : '.json,.gz,.json.gz'} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid={`upload-input-${source.key}`} />
          {busy ? <div className="text-sm aampl-navy-text">Uploading…</div> : (<><UploadIcon size={28} className="mx-auto aampl-navy-text mb-2" /><div className="text-sm font-medium aampl-navy-text">{latest ? 'Replace File' : isSheet ? 'Drag & drop the filled sheet' : 'Drag & drop JSON'}</div><div className="text-xs text-slate-500 mt-1">{isSheet ? '.xlsx or .csv — or click to browse' : '.json or .json.gz — or click to browse'}</div></>)}
        </div>
      ) : (
        <div className="text-xs text-slate-500 bg-slate-50 border border-[var(--aampl-border)] rounded p-3">Restricted to admin or {source.dept} HOD.</div>
      )}
    </div>
  );
}
